import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { Popover } from "@mui/material";
import dayjs from "dayjs";
import { MouseEvent, useState } from "react";
import styled from "styled-components";
import {
  MaterialRequestFilterType,
  valueToText,
} from "../../../../configs/filterTypes";
import { OrderDetailProgress } from "../../../../data/entities";
import { color } from "../../../../shared/styles";

type Tone = "neutral" | "warning" | "success";

const ProgressButton = styled.button`
  width: 100%;
  min-width: 0;
  max-width: 156px;
  box-sizing: border-box;
  padding: 6px 7px;
  border: 1px solid ${color.emphasis.NEUTRAL_N30};
  border-radius: 6px;
  background: ${color.emphasis.NEUTRAL_N0};
  color: ${color.secondary.CHARCOAL};
  cursor: pointer;
  display: grid;
  gap: 3px;
  overflow: hidden;
  font-family: "Noto Sans";
  text-align: left;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease,
    transform 140ms ease;

  &:hover,
  &:focus-visible {
    border-color: ${color.primary.BLUE_B90};
    box-shadow: 0 4px 12px rgba(19, 104, 188, 0.14);
    outline: none;
    transform: translateY(-1px);
  }
`;

const ProgressLine = styled.span`
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: 15px minmax(0, 1fr) auto 8px;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  line-height: 15px;

  svg {
    width: 14px;
    height: 14px;
    color: ${color.emphasis.NEUTRAL_N70};
  }
`;

const ProgressLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${color.emphasis.NEUTRAL_N70};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgressValue = styled.span`
  color: ${color.secondary.CHARCOAL};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ToneDot = styled.span<{ $tone: Tone }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $tone }) =>
    $tone === "success"
      ? color.status.SUCCESS
      : $tone === "warning"
        ? color.status.WARNING
        : color.emphasis.NEUTRAL_N50};
`;

const MaterialLine = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 156px;
  margin-bottom: 3px;
  overflow: hidden;
  color: ${color.emphasis.NEUTRAL_N70};
  font-size: 10px;
  line-height: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    color: ${color.secondary.CHARCOAL};
  }
`;

const PopoverContent = styled.div`
  width: 380px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 96px);
  box-sizing: border-box;
  overflow-y: auto;
  padding: 14px;
  font-family: "Noto Sans";
  color: ${color.secondary.CHARCOAL};
`;

const PopoverHeader = styled.div`
  display: grid;
  gap: 2px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${color.emphasis.NEUTRAL_N10};
`;

const PopoverTitle = styled.div`
  font-size: 15px;
  line-height: 20px;
  font-weight: 800;
`;

const PopoverHint = styled.div`
  color: ${color.emphasis.NEUTRAL_N70};
  font-size: 11px;
`;

const Section = styled.section`
  min-width: 0;
  padding-top: 12px;
`;

const SectionTitle = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 17px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  span:last-child {
    color: ${color.emphasis.NEUTRAL_N70};
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    white-space: nowrap;
  }
`;

const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${color.emphasis.NEUTRAL_N10};
  border-radius: 8px;
  overflow: hidden;
`;

const DetailRow = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  background: ${color.emphasis.NEUTRAL_N0};

  &:not(:last-child) {
    border-bottom: 1px solid ${color.emphasis.NEUTRAL_N10};
  }
`;

const DetailIdentity = styled.div`
  min-width: 0;
  font-size: 12px;
  line-height: 17px;

  strong {
    display: block;
  }

  span {
    display: block;
    overflow: hidden;
    color: ${color.emphasis.NEUTRAL_N70};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DetailState = styled.div`
  max-width: 140px;
  text-align: right;
  font-size: 11px;
  line-height: 16px;

  strong {
    display: block;
    overflow-wrap: anywhere;
  }

  span {
    color: ${color.emphasis.NEUTRAL_N70};
  }
`;

const EmptyState = styled.div`
  padding: 12px;
  color: ${color.emphasis.NEUTRAL_N70};
  background: ${color.emphasis.NEUTRAL_N5};
  font-size: 12px;
  text-align: center;
`;

const getTone = (status?: string | null): Tone => {
  if (status === "ALL_IMPORTED" || status === "ALL_EXPORTED") return "success";
  if (status === "PARTIALLY_IMPORTED" || status === "PARTIALLY_EXPORTED")
    return "warning";
  return "neutral";
};

const importStatusLabel: Record<string, string> = {
  NO_IMPORT_REQUEST: "Chưa có đề nghị",
  NOT_IMPORTED: "Chưa nhập",
  PARTIALLY_IMPORTED: "Đã nhập một phần",
  ALL_IMPORTED: "Đã nhập đủ",
};

const exportStatusLabel: Record<string, string> = {
  NO_DELIVERY_TRIP: "Chưa có chuyến",
  NOT_EXPORTED: "Chưa xuất",
  PARTIALLY_EXPORTED: "Xuất một phần",
  ALL_EXPORTED: "Đã xuất",
  CANCELLED: "Đã huỷ",
};

const deliveryStatusLabel: Record<string, string> = {
  PENDING: "Chờ xuất",
  SCHEDULED: "Đã lên lịch",
  IN_TRANSIT: "Đang vận chuyển",
  DELIVERED: "Đã giao",
  RETURNED: "Có hàng trả về",
  CANCELLED: "Đã huỷ",
};

const formatQuantity = (value: number) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(
    value || 0,
  );

interface DetailProgressCellProps {
  progress?: OrderDetailProgress;
  materialStatus?: string;
}

const DetailProgressCell = ({
  progress,
  materialStatus,
}: DetailProgressCellProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  if (!progress) {
    return materialStatus ? (
      <MaterialLine>
        Vật tư:{" "}
        <strong>
          {valueToText(MaterialRequestFilterType, "status", materialStatus)}
        </strong>
      </MaterialLine>
    ) : (
      <span>—</span>
    );
  }

  const imports = progress.import_requests;
  const trips = progress.delivery_trips;

  const openPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const importCount = imports?.total
    ? `${imports.completed}/${imports.total}`
    : "—";
  const importSummary = imports?.total
    ? `${importCount} đã nhập`
    : "Chưa có đề nghị";
  const tripCount = !trips?.applicable
    ? "—"
    : trips.active_total
      ? `${trips.exported}/${trips.active_total}`
      : "—";
  const tripSummary = !trips?.applicable
    ? "Không áp dụng"
    : trips.active_total
      ? `${tripCount} đã xuất`
      : "Chưa có chuyến";

  return (
    <>
      {materialStatus && (
        <MaterialLine>
          Vật tư:{" "}
          <strong>
            {valueToText(MaterialRequestFilterType, "status", materialStatus)}
          </strong>
        </MaterialLine>
      )}
      <ProgressButton
        type="button"
        onClick={openPopover}
        aria-label={`Xem trạng thái chi tiết đơn hàng: nhập kho ${importSummary}, chuyến xe ${tripSummary}`}
      >
        <ProgressLine>
          <Inventory2OutlinedIcon />
          <ProgressLabel>Nhập kho</ProgressLabel>
          <ProgressValue>{importCount}</ProgressValue>
          <ToneDot $tone={getTone(imports?.status)} />
        </ProgressLine>
        <ProgressLine>
          <LocalShippingOutlinedIcon />
          <ProgressLabel>Chuyến xe</ProgressLabel>
          <ProgressValue>{tripCount}</ProgressValue>
          <KeyboardArrowRightIcon aria-hidden="true" />
        </ProgressLine>
      </ProgressButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "10px",
            border: `1px solid ${color.emphasis.NEUTRAL_N10}`,
            boxShadow: "0 14px 38px rgba(13, 12, 12, 0.16)",
          },
        }}
      >
        <PopoverContent>
          <PopoverHeader>
            <PopoverTitle>Tiến độ vận hành</PopoverTitle>
            <PopoverHint>Chi tiết theo từng phát sinh</PopoverHint>
          </PopoverHeader>

          <Section>
            <SectionTitle>
              <span>Đề nghị nhập kho</span>
              <span>{importSummary}</span>
            </SectionTitle>
            <DetailList>
              {imports.items.length === 0 ? (
                <EmptyState>Chưa có đề nghị nhập kho</EmptyState>
              ) : (
                imports.items.map((item) => {
                  const productNames = item.products
                    .map((product) => product.name)
                    .join(", ");
                  return (
                    <DetailRow key={item.id}>
                      <DetailIdentity>
                        <strong>
                          {item.import_code || "Đề nghị nhập kho"}
                        </strong>
                        <span>{productNames || "Chưa có sản phẩm"}</span>
                      </DetailIdentity>
                      <DetailState>
                        <strong>
                          {importStatusLabel[item.status] || item.status}
                        </strong>
                        <span>
                          {formatQuantity(item.imported_quantity)}/
                          {formatQuantity(item.required_quantity)}
                        </span>
                      </DetailState>
                    </DetailRow>
                  );
                })
              )}
            </DetailList>
          </Section>

          {trips?.applicable && (
            <Section>
              <SectionTitle>
                <span>Chuyến xe</span>
                <span>{tripSummary}</span>
              </SectionTitle>
              <DetailList>
                {trips.items.length === 0 ? (
                  <EmptyState>Chưa có chuyến xe</EmptyState>
                ) : (
                  trips.items.map((trip) => (
                    <DetailRow key={trip.id}>
                      <DetailIdentity>
                        <strong>{trip.vehicle_plate}</strong>
                        <span>
                          {trip.scheduled_at
                            ? dayjs(trip.scheduled_at).format(
                                "DD/MM/YYYY HH:mm",
                              )
                            : "Chưa có lịch"}
                        </span>
                      </DetailIdentity>
                      <DetailState>
                        <strong>
                          {exportStatusLabel[trip.export_status] ||
                            trip.export_status}
                        </strong>
                        <span>
                          {deliveryStatusLabel[trip.delivery_status] ||
                            trip.delivery_status}
                        </span>
                      </DetailState>
                    </DetailRow>
                  ))
                )}
              </DetailList>
            </Section>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};

export default DetailProgressCell;
